import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Palette, X } from 'lucide-react';
import { useClientDataContext } from '../contexts/ClientDataContext';
import { usePreviewMode } from '../hooks/usePreviewMode';
import { ThemeColors } from '../types';

const THEME_COLOR_FIELDS: { key: keyof ThemeColors; label: string; used: boolean }[] = [
  { key: 'header_background', label: 'Header bar background', used: true },
  { key: 'header_text_color', label: 'Header text', used: false },
  { key: 'header_shop_background', label: 'Shop button background', used: false },
  { key: 'header_shop_color', label: 'Shop button text', used: false },
  { key: 'header_cart_background', label: 'Cart icon / badge / avatar bg', used: true },
  { key: 'header_cart_text_color', label: 'Cart badge / avatar text', used: true },
  { key: 'header_account_text_color', label: 'Login name color', used: false },
  { key: 'left_side_background', label: 'Sidebar background', used: true },
  { key: 'left_side_heading_color', label: 'Sidebar heading', used: true },
  { key: 'left_side_color', label: 'Sidebar item text', used: true },
  { key: 'left_side_active_color', label: 'Sidebar active item text', used: true },
];

export function ThemeDebugOverlay() {
  const { isPreviewMode } = usePreviewMode();
  const { clientData } = useClientDataContext();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const debugEnabled = new URLSearchParams(location.search).get('debug') === 'true';
  if (!isPreviewMode || !debugEnabled) return null;

  const themeColors = clientData?.themeColors || {};

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(themeColors, null, 2));
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="w-11 h-11 rounded-full bg-gray-800 text-white shadow-lg flex items-center justify-center hover:bg-gray-700 focus:outline-none"
        title="Theme colors debug"
        aria-label="Open theme colors debug panel"
      >
        <Palette className="w-5 h-5" aria-hidden="true" />
      </button>

      {isOpen && (
        <div
          className="fixed bottom-6 right-6 z-[9999] w-96 max-h-[75vh] overflow-y-auto rounded-lg border border-gray-300 bg-white text-gray-900 shadow-2xl text-xs font-mono"
          style={{ colorScheme: 'light' }}
        >
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-200 bg-gray-100 sticky top-0">
            <span className="font-bold">Theme colors — raw from API</span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={handleCopy}
                className="px-2 py-1 rounded bg-gray-800 text-white hover:bg-gray-700"
              >
                Copy JSON
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="p-1 rounded hover:bg-gray-200"
                aria-label="Close theme colors debug panel"
              >
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {THEME_COLOR_FIELDS.map(({ key, label, used }) => {
              const value = themeColors[key];
              return (
                <div key={key} className="flex items-center gap-2 px-3 py-2">
                  <div
                    className="w-6 h-6 rounded flex-shrink-0 border border-gray-300"
                    style={{
                      backgroundColor: value || undefined,
                      backgroundImage: !value
                        ? 'repeating-conic-gradient(#d1d5db 0% 25%, #f3f4f6 0% 50%) 50% / 8px 8px'
                        : undefined,
                    }}
                    title={value || 'not set'}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate">{key}</div>
                    <div className="text-gray-400 truncate">
                      {label}
                      {!used && <span className="text-amber-600"> — unused by storefront</span>}
                    </div>
                  </div>
                  <div className="whitespace-nowrap text-right">
                    {value || <span className="text-gray-400">empty</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
